import { supabase } from '../lib/supabase';
import { ArtCategory } from '../types';
import { assertContentIsAllowed } from '../lib/contentModeration';

export const collaborationsService = {
  async getCollaborations(options?: {
    category?: ArtCategory | 'all';
    location?: string;
    status?: 'open' | 'in_progress' | 'completed' | 'cancelled' | 'all';
    limit?: number;
    offset?: number;
  }) {
    let query = supabase
      .from('collaborations')
      .select(`
        *,
        profiles!collaborations_author_id_fkey (
          id,
          username,
          full_name,
          avatar_url,
          is_premium
        ),
        collaboration_applications (
          id,
          applicant_id,
          status,
          profiles!collaboration_applications_applicant_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `);

    // Filters
    if (options?.category && options.category !== 'all') {
      query = query.eq('category', options.category);
    }
    
    if (options?.status && options.status !== 'all') {
      query = query.eq('status', options.status);
    }
    
    if (options?.location) {
      query = query.ilike('location', `%${options.location}%`);
    }

    // Sort by created date
    query = query.order('created_at', { ascending: false });

    // Pagination
    if (options?.limit) {
      query = query.limit(options.limit);
    }
    if (options?.offset) {
      query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
    }

    const { data, error } = await query;
    if (error) throw error;

    return data;
  },

  async createCollaboration(collaborationData: {
    title: string;
    description: string;
    category: ArtCategory;
    location?: string;
    budget?: number;
    deadline?: string;
    requirements?: string[];
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([
      collaborationData.title,
      collaborationData.description,
      collaborationData.location,
      Array.isArray(collaborationData.requirements) ? collaborationData.requirements.join(' ') : undefined,
    ]);

    const { data, error } = await supabase
      .from('collaborations')
      .insert({
        author_id: user.id,
        title: collaborationData.title,
        description: collaborationData.description,
        category: collaborationData.category,
        location: collaborationData.location || null,
        budget: collaborationData.budget || null,
        deadline: collaborationData.deadline || null,
        requirements: collaborationData.requirements || [],
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async applyToCollaboration(collaborationId: string, message: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([message]);

    const { data, error } = await supabase
      .from('collaboration_applications')
      .insert({
        collaboration_id: collaborationId,
        applicant_id: user.id,
        message,
      })
      .select(`
        *,
        profiles!collaboration_applications_applicant_id_fkey (
          username,
          full_name,
          avatar_url
        )
      `)
      .single();

    if (error) throw error;
    return data;
  },

  async getMyCollaborations() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('collaborations')
      .select(`
        *,
        collaboration_applications (
          id,
          applicant_id,
          message,
          status,
          created_at,
          profiles!collaboration_applications_applicant_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('author_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async getMyApplications() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('collaboration_applications')
      .select(`
        *,
        collaborations (
          id,
         author_id,
          title,
          description,
          category,
          status,
          profiles!collaborations_author_id_fkey (
            username,
            full_name,
            avatar_url
          )
        )
      `)
      .eq('applicant_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data;
  },

  async updateApplicationStatus(applicationId: string, status: 'accepted' | 'rejected') {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // First, get the application to verify ownership
    const { data: application, error: fetchError } = await supabase
      .from('collaboration_applications')
      .select(`
        *,
        collaborations!collaboration_applications_collaboration_id_fkey (
          author_id
        )
      `)
      .eq('id', applicationId)
      .single();

    if (fetchError) throw fetchError;
    
    // Verify that the current user is the owner of the collaboration
    if (application.collaborations.author_id !== user.id) {
      throw new Error('No tienes permisos para actualizar esta aplicación');
    }

    // Update the application status
    const { data, error } = await supabase
      .from('collaboration_applications')
      .update({ 
        status,
        updated_at: new Date().toISOString()
      })
      .eq('id', applicationId)
      .select()
      .single();

    if (error) throw error;

    return data;
  },

  async updateCollaboration(
    collaborationId: string,
    updates: {
      title?: string;
      description?: string;
      category?: ArtCategory;
      location?: string;
      budget?: number;
      deadline?: string;
      requirements?: string[];
      status?: 'open' | 'in_progress' | 'completed' | 'cancelled';
    }
  ) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    await assertContentIsAllowed([
      updates.title,
      updates.description,
      updates.location,
      Array.isArray(updates.requirements) ? updates.requirements.join(' ') : undefined,
    ]);

    const { data, error } = await supabase
      .from('collaborations')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', collaborationId)
      .eq('author_id', user.id) // Ensure user can only edit their own collaborations
      .select()
      .single();

    if (error) throw error;
    return data;
  },
};
